import type { EventType } from '@fellowship/shared'
import { Check } from 'lucide-react'

const BASE = 'https://images.unsplash.com/'
const Q = '?auto=format&fit=crop&w=600&h=400&q=75'

// 6 curated options per event type. IDs labelled 1–6 so the user can
// quickly identify broken ones by number.
const TYPE_IMAGES: Record<string, { id: string; label: string }[]> = {
  potluck: [
    { id: 'photo-1529156069898-49953e39b3ac', label: '1' },
    { id: 'photo-1504674900247-0877df9cc836', label: '2' },
    { id: 'photo-1414235077428-338989a2e8c0', label: '3' },
    { id: 'photo-1547592180-85f173990554', label: '4' },
    { id: 'photo-1555939594-58d7cb561ad1', label: '5' },
    { id: 'photo-1565299624946-b28f40a0ae38', label: '6' },
  ],
  worship: [
    { id: 'photo-1507692049790-de58290a4334', label: '1' },
    { id: 'photo-1438232992991-995b7058bbb3', label: '2' },
    { id: 'photo-1520637836862-4d197d17c8a4', label: '3' },
    { id: 'photo-1510915228340-29c85a43dcfe', label: '4' },
    { id: 'photo-1532375810709-75b1da00537c', label: '5' },
    { id: 'photo-1445116572660-236099ec97a0', label: '6' },
  ],
  study: [
    { id: 'photo-1481627834876-b7833e8f5570', label: '1' },
    { id: 'photo-1522202176988-66273c2fd55f', label: '2' },
    { id: 'photo-1456513080510-7bf3a84b82f8', label: '3' },
    { id: 'photo-1434030216411-0b793f4b4173', label: '4' },
    { id: 'photo-1491841550275-ad7854e35ca6', label: '5' },
    { id: 'photo-1507003211169-0a1dd7228f2d', label: '6' },
  ],
  social: [
    { id: 'photo-1528605248644-14dd04022da1', label: '1' },
    { id: 'photo-1491438590914-bc09fcaaf77a', label: '2' },
    { id: 'photo-1529543544282-ea669407fca3', label: '3' },
    { id: 'photo-1516450360452-9312f5e86fc7', label: '4' },
    { id: 'photo-1511632765153-994ad668debbe', label: '5' },
    { id: 'photo-1543269865-cbf427effbad', label: '6' },
  ],
  outdoors: [
    { id: 'photo-1551632811-561732d1e306', label: '1' }, // hiking
    { id: 'photo-1681295692638-97ace05f56b4', label: '2' }, // biking
    { id: 'photo-1758798460018-d86989b9524b', label: '3' }, // sports
    { id: 'photo-1642933196504-62107dac9258', label: '4' }, // rafting
    { id: 'photo-1574744918163-6cef6f4a31b0', label: '5' }, // swimming
    { id: 'photo-1475483768296-6163e08872a1', label: '6' }, // camping
  ],
  kids: [
    { id: 'photo-1503454537195-1dcabb73ffb9', label: '1' },
    { id: 'photo-1488521787991-ed7bbaae773c', label: '2' },
    { id: 'photo-1472162072942-cd5147eb3902', label: '3' },
    { id: 'photo-1516627145497-ae6968895b74', label: '4' },
    { id: 'photo-1528360983277-13d401cdc186', label: '5' },
    { id: 'photo-1596464716127-f2a82984de30', label: '6' },
  ],
  service: [
    { id: 'photo-1559027615-cd4628902d4a', label: '1' },
    { id: 'photo-1469571486292-0ba58a3f068b', label: '2' },
    { id: 'photo-1593113598332-cd288d649433', label: '3' },
    { id: 'photo-1509099836639-18ba1795216d', label: '4' },
    { id: 'photo-1544027993-37dbfe43562a', label: '5' },
    { id: 'photo-1573496359142-b8d87734a5a2', label: '6' },
  ],
}

export function getDefaultImageForType(eventType: string, size = '600x400'): string {
  const images = TYPE_IMAGES[eventType] ?? TYPE_IMAGES.potluck
  const [w, h] = size.split('x')
  return `${BASE}${images[0].id}?auto=format&fit=crop&w=${w}&h=${h}&q=75`
}

interface EventImagePickerProps {
  eventType: EventType | string
  value: string | null
  onChange: (url: string | null) => void
}

export function EventImagePicker({ eventType, value, onChange }: EventImagePickerProps) {
  const images = TYPE_IMAGES[eventType] ?? TYPE_IMAGES.potluck

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {images.map((img) => {
          const url = `${BASE}${img.id}${Q}`
          const isSelected = value === url
          return (
            <button
              key={img.id}
              type="button"
              onClick={() => onChange(isSelected ? null : url)}
              style={{
                position: 'relative',
                padding: 0,
                border: `2px solid ${isSelected ? 'var(--primary)' : 'transparent'}`,
                borderRadius: 10,
                overflow: 'hidden',
                cursor: 'pointer',
                aspectRatio: '3/2',
                background: 'var(--canvas)',
              }}
            >
              <img
                src={url}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {/* Number badge — remove once images are confirmed */}
              <span style={{
                position: 'absolute',
                top: 5,
                left: 6,
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                borderRadius: 4,
                padding: '1px 5px',
                lineHeight: 1.6,
                pointerEvents: 'none',
              }}>
                {img.label}
              </span>
              {isSelected && (
                <span style={{
                  position: 'absolute',
                  top: 5,
                  right: 6,
                  background: 'var(--primary)',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Check size={12} color="#fff" strokeWidth={3} />
                </span>
              )}
            </button>
          )
        })}
      </div>
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          Remove photo
        </button>
      )}
    </div>
  )
}
